import Image from 'next/image';
import { useMemo } from 'react';
import { NETWORKS } from 'src/common/types/network';

import { Badge, KeyValueRow, KeyValueTable } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';

import { HypurrNetPoolPositionsStrategy, Trigger } from '../../domain';
import { AddressChip } from './components/AddressChip';
import { CurrencyDisplay } from './components/CurrencyDisplay';

interface Props {
  trigger: Trigger;
}

export function HypurrNetPoolPositionsTriggerFullDetails({ trigger }: Props) {
  const { getChainIconUrl } = useGetChainInfo();

  const hyTokenAddress = useMemo(() => {
    return trigger.context?.hyTokenAddress || '';
  }, [trigger.context]);

  const variableDebtTokenAddress = useMemo(() => {
    return trigger.context?.variableDebtTokenAddress || '';
  }, [trigger.context]);

  const volumeCurrencyAddress = useMemo(() => {
    return trigger.context?.volumeCurrencyAddress || '';
  }, [trigger.context]);

  const chainId = useMemo(() => {
    return trigger.context?.chainId || 999;
  }, [trigger.context]);

  const strategy = useMemo(() => {
    return trigger.context?.strategy;
  }, [trigger.context]);

  const networkInfo = useMemo(() => {
    return NETWORKS.find((network) => network.chainId === chainId);
  }, [chainId]);

  const networkDisplay = useMemo(() => {
    if (!networkInfo) return '-';
    const chainIconUrl = getChainIconUrl(networkInfo.chainId);

    return (
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
        {chainIconUrl && <Image src={chainIconUrl} alt={networkInfo.name} width={20} height={20} />}
        <span style={{ textTransform: 'capitalize' }}>{networkInfo.name.replace(/-/g, ' ')}</span>
      </div>
    );
  }, [networkInfo, getChainIconUrl]);

  const hyTokenDisplay = useMemo(() => {
    if (!hyTokenAddress || !networkInfo) return '-';

    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <AddressChip address={hyTokenAddress} chainId={networkInfo.chainId} />
      </div>
    );
  }, [hyTokenAddress, networkInfo]);

  const variableDebtTokenDisplay = useMemo(() => {
    if (!variableDebtTokenAddress || !networkInfo) return '-';

    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <AddressChip address={variableDebtTokenAddress} chainId={networkInfo.chainId} />
      </div>
    );
  }, [variableDebtTokenAddress, networkInfo]);

  const volumeCurrencyDisplay = useMemo(() => {
    if (!volumeCurrencyAddress || !networkInfo) return '-';

    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <CurrencyDisplay value={volumeCurrencyAddress} chainId={networkInfo.chainId} />
      </div>
    );
  }, [volumeCurrencyAddress, networkInfo]);

  const strategyDisplay = useMemo(() => {
    if (!strategy) return '-';

    const color = strategy === HypurrNetPoolPositionsStrategy.Deposit ? 'success' : 'warning';
    const label = strategy === HypurrNetPoolPositionsStrategy.Deposit ? 'Deposit' : 'Borrow';

    return <Badge variant={color}>{label}</Badge>;
  }, [strategy]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow label="Strategy" value={strategyDisplay} />
      <KeyValueRow label="hyToken Address" value={hyTokenDisplay} />
      <KeyValueRow label="VariableDebtToken Address" value={variableDebtTokenDisplay} />
      <KeyValueRow label="Volume Currency" value={volumeCurrencyDisplay} />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
}
